import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Button,
  useTheme,
  useMediaQuery,
  MenuItem,
  Select,
  FormControl,
  Pagination,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useDelete from "../../../../api/useDelete";
import SkeletonLoading from "./loading/SkeletonLoading";
import { useTranslation } from "react-i18next";
import UpdateProcedureDialog from "./UpdateProcedureDialog";
import { useNavigate } from "react-router-dom";

const ListProcedures = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalDentalProcedures, setTotalDentalProcedures] = useState(0);

  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["dental-procedure", page, rowsPerPage],
    `dental-procedure/?page=${page}&page_size=${rowsPerPage}&search=${searchTerm}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, refetch, page, rowsPerPage]);

  useEffect(() => {
    if (data && data.count !== undefined) {
      setTotalDentalProcedures(data.count);
    }
  }, [data]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const { handleDelete, ConfirmDialog } = useDelete("dental-procedure", token);

  const handleEdit = (procedure) => {
    setSelectedProcedure(procedure);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProcedure(null);
  };

  const tableCellStyle = {
    fontWeight: "bold",
    textAlign: "center",
  };

  const tableStyle = {
    textAlign: "center",
  };

  return (
    <Paper
      sx={{
        padding: "30px 40px",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          {t("dental.listProcedures")}
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/dental/procedures/add")}
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            paddingX: 3,
            paddingY: isSmallScreen ? 1 : 1.5,
            alignSelf: isSmallScreen ? "flex-end" : "center",
            borderRadius: "8px",
            boxShadow: 3,
            transition: "all 0.3s ease",
            backgroundColor: theme.palette.primary,
            "&:hover": {
              backgroundColor: theme.palette.primary,
              boxShadow: 6,
              transform: "translateY(-2px)",
            },
            "&:active": {
              boxShadow: 2,
              transform: "translateY(0px)",
            },
            "& .MuiButton-startIcon": {
              marginRight: 1,
            },
          }}
        >
          {t("pharmacy.addNew")}
        </Button>
      </Box>

      <TextField
        label={t("dental.search")}
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 4 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        placeholder={t("dental.searchByNameOrDescription")}
      />

      {isLoading && <SkeletonLoading />}
      {isError && (
        <Typography color="error" mt={2}>
          {t("dental.errorLoadingData", { error: error.message })}
        </Typography>
      )}

      {data?.results && (
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead style={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell style={tableCellStyle}>{t("dental.name")}</TableCell>
                <TableCell style={tableCellStyle}>
                  {t("dental.description")}
                </TableCell>
                <TableCell style={tableCellStyle}>{t("dental.cost")}</TableCell>
                <TableCell style={tableCellStyle}>
                  {t("dental.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {data?.results?.map((procedure) => (
                <TableRow
                  key={procedure.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "#f9f9f9" },
                    transition: "0.3s",
                  }}
                >
                  <TableCell style={tableStyle}>{procedure.name}</TableCell>
                  <TableCell style={tableStyle}>
                    {procedure.description?.length > 50
                      ? `${procedure.description.slice(0, 50)}...`
                      : procedure.description || t("dental.noDescription")}
                  </TableCell>
                  <TableCell style={tableStyle}>
                    {procedure.cost
                      ? `${procedure.cost}؋`
                      : t("dental.notAvailable")}
                  </TableCell>
                  <TableCell style={tableStyle}>
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(procedure)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(procedure.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination Controls */}
      <Box display="flex" justifyContent="space-between" mt={4} mb={2}>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" mr={2}>
            {t("dental.rowsPerPage")}:
          </Typography>
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <Select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              label={t("dental.rowsPerPage")}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={500}>500</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalDentalProcedures / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          color="primary"
          variant="outlined"
          shape="rounded"
        />
      </Box>

      <ConfirmDialog />

      {!isLoading && data?.length === 0 && (
        <Typography variant="body1" color="textSecondary" mt={2}>
          {t("dental.noProceduresFound")}
        </Typography>
      )}

      {selectedProcedure && (
        <UpdateProcedureDialog
          open={openDialog}
          onClose={handleCloseDialog}
          procedure={selectedProcedure}
          token={token}
          refetch={refetch}
        />
      )}
    </Paper>
  );
};

export default ListProcedures;
